import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center" data-testid="forgot-logo">
          <div className="leading-none">
            <div className="flex items-baseline gap-0.5">
              <span className="font-heading text-2xl font-black text-[#212121] tracking-tight">DFTB</span>
              <span className="font-heading text-2xl font-black text-[#28BBE3] tracking-tight">26</span>
            </div>
            <div className="text-center text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-1">Virtual</div>
          </div>
        </Link>

        <div className="rounded-3xl bg-white ring-1 ring-black/5 p-8 sm:p-10 shadow-xl shadow-slate-200/40">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Password reset</div>
          <h1 className="mt-2 font-heading text-3xl tracking-tighter font-medium text-slate-900">
            Forgot your password?
          </h1>

          {sent ? (
            <div className="mt-6 space-y-4" data-testid="forgot-sent">
              <p className="text-sm text-slate-600 leading-relaxed">
                If an account exists for <span className="font-semibold text-slate-900">{email}</span>,
                you'll receive an email with a link to reset your password. The link expires in 1 hour.
              </p>
              <p className="text-xs text-slate-500">
                Didn't get it? Check your spam folder, or email{" "}
                <a href="mailto:registration@dontforgetthebubbles.com" className="text-[#28BBE3] hover:underline">
                  registration@dontforgetthebubbles.com
                </a>.
              </p>
              <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#28BBE3] hover:underline font-semibold" data-testid="forgot-back-link">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="mt-2 text-sm text-slate-600">
                Enter the email you used to register and we'll send you a reset link.
              </p>
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <Label htmlFor="forgot-email" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
                  <Input id="forgot-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 rounded-2xl border-slate-200 bg-white py-6"
                    placeholder="you@hospital.org" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white py-6 font-semibold"
                  data-testid="forgot-submit-btn">
                  {loading ? "Sending…" : "Send reset link"}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </form>
              <p className="mt-6 text-sm text-slate-600 text-center">
                Remembered it?{" "}
                <Link to="/login" className="text-[#28BBE3] hover:underline font-semibold" data-testid="forgot-signin-link">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
